import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, Shield, Zap, Target, BarChart3, Users } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { LeadMagnetForm } from '@/components/LeadMagnetForm';
import { LogoCarousel } from '@/components/ui/logo-carousel';
import { GradientHeading } from '@/components/ui/gradient-heading';
import { type SVGProps } from 'react';
import { RetailIcon, FashionIcon, ElectronicsIcon, TourismIcon, AutomotiveIcon, RealEstateIcon } from '@/components/IndustryIcons';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import { SEOOptimizer } from '@/components/SEOOptimizer';
import { VideoModal } from '@/components/VideoModal';
import { MarqueeDemo } from '@/components/MarqueeDemo';
import { ScheduleButton } from '@/components/ScheduleButton';
import { HeroSection } from '@/components/HeroSection';
// Hero image imported via inline URL

const Index = () => {
  const { t, language } = useLanguage();

  // Industries data with bilingual names
  const industries = [
    { name: language === 'en' ? 'Consumer Electronics' : 'Electrónicos de Consumo', id: 1, img: ElectronicsIcon },
    { name: language === 'en' ? 'Fashion & Apparel' : 'Moda y Vestimenta', id: 2, img: FashionIcon },
    { name: language === 'en' ? 'Retail & Supermarkets' : 'Retail y Supermercados', id: 3, img: RetailIcon },
    { name: language === 'en' ? 'Software & SaaS Companies' : 'Empresas de Software y SaaS', id: 4, img: ElectronicsIcon },
    { name: language === 'en' ? 'Industrial Manufacturing' : 'Manufactura Industrial', id: 5, img: AutomotiveIcon },
    { name: language === 'en' ? 'Consumer Packaged Goods (CPG)' : 'Bienes de Consumo Empaquetados', id: 6, img: RetailIcon },
    { name: language === 'en' ? 'Beauty, Health & Personal Care' : 'Belleza, Salud y Cuidado Personal', id: 7, img: FashionIcon },
    { name: language === 'en' ? 'Food & Beverage (incl. Health & Organic)' : 'Alimentos y Bebidas (incl. Saludable y Orgánico)', id: 8, img: RetailIcon },
    { name: language === 'en' ? 'Tourism, Hospitality & Pet Care' : 'Turismo, Hospitalidad y Cuidado de Mascotas', id: 9, img: TourismIcon },
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: language === 'en' ? 'Stop Hidden Margin Leaks' : 'Detén Fugas de Margen Ocultas',
      description: language === 'en' ? 'Detect pricing gaps and uncover new revenue opportunities in real time' : 'Detecta brechas de precios y descubre nuevas oportunidades de ingresos en tiempo real',
    },
    {
      icon: Zap,
      title: language === 'en' ? 'React Before Your Competitors Do' : 'Reacciona Antes Que Tus Competidores',
      description: language === 'en' ? 'Get instant alerts on competitor price moves and adjust with confidence' : 'Recibe alertas instantáneas sobre movimientos de precios de competidores y ajusta con confianza',
    },
    {
      icon: Shield,
      title: language === 'en' ? 'Build Fair & Defendable Pricing Models' : 'Construye Modelos de Precios Justos y Defendibles',
      description: language === 'en' ? 'Design strategies based on value, not guesswork, and create lasting advantages' : 'Diseña estrategias basadas en valor, no en conjeturas, y crea ventajas duraderas',
    },
  ];

  return (
    <>
      <SEOOptimizer 
        routeKey="home"
        title={language === 'en' 
          ? "PriceZing - Transform Your Pricing Strategy | Expert Consulting & AI Solutions"
          : "PriceZing - Transforma Tu Estrategia de Precios | Consultoría Experta y Soluciones IA"
        }
        description={language === 'en'
          ? "Maximize profits with strategic pricing solutions. Expert consulting, AI-powered monitoring, and certified market research for LATAM & U.S. businesses."
          : "Maximiza ganancias con soluciones estratégicas de precios. Consultoría experta, monitoreo IA e investigación certificada para empresas LATAM y EE.UU."
        }
        type="website"
      />
      <main className="bg-background">
        {/* Hero Section */}
        <HeroSection />

        {/* Benefits Section */}
      <section id="benefits" className="py-20 bg-muted/30 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-10 left-10 w-32 h-32 bg-primary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '0s' }}></div>
          <div className="absolute top-32 right-20 w-24 h-24 bg-accent/15 rounded-full blur-2xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-secondary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '4s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text">
              {language === 'en' ? 'Why Companies Trust PriceZing' : 'Por Qué las Empresas Confían en PriceZing'}
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {language === 'en' ? 'Helping 300+ companies in LATAM & the U.S. protect margins and grow profits' : 'Ayudando a 300+ empresas en LATAM y EE.UU. a proteger márgenes y hacer crecer ganancias'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index} 
                  className="animate-slide-up group"
                  style={{ animationDelay: `${index * 200}ms` }}
                >
                   <Card className="text-center shadow-soft hover:shadow-medium transition-all duration-500 group-hover:scale-105 group-hover:-translate-y-2 bg-background/80 backdrop-blur-sm border-2 border-transparent hover:border-primary/20 relative overflow-hidden">
                     {/* Animated background gradient */}
                     <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    <CardHeader className="relative z-10">
                      <div className="mx-auto mb-4 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 relative overflow-hidden">
                        {/* Spinning ring effect */}
                        <div className="absolute inset-0 rounded-full border-2 border-primary/30 opacity-0 group-hover:opacity-100 group-hover:animate-spin transition-opacity duration-500"></div>
                        <Icon className="w-8 h-8 text-primary group-hover:scale-110 transition-transform duration-300 relative z-10" />
                      </div>
                      <CardTitle className="text-xl group-hover:text-primary transition-colors duration-300">{benefit.title}</CardTitle>
                    </CardHeader>
                    <CardContent className="relative z-10">
                      <CardDescription className="text-base group-hover:text-foreground/80 transition-colors duration-300">{benefit.description}</CardDescription>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marquee Component */}
      <MarqueeDemo />

      {/* Industries Carousel */}
      <section id="industries" className="py-16 relative overflow-hidden">
        {/* Dynamic background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full">
            {[...Array(6)].map((_, i) => (
              <div 
                key={i}
                className="absolute bg-gradient-to-r from-primary/10 to-transparent"
                style={{
                  top: `${i * 16.67}%`,
                  left: '-50%',
                  width: '200%',
                  height: '2px',
                  transform: `rotate(${i * 30}deg)`,
                  transformOrigin: 'center',
                  animation: `slide 8s ease-in-out infinite`,
                  animationDelay: `${i * 1.3}s`
                }}
              ></div>
            ))}
          </div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="mx-auto flex w-full max-w-4xl flex-col items-center space-y-6">
            <div className="text-center animate-slide-up">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent animate-pulse">
                {language === 'en' ? 'Industries We Serve' : 'Industrias que Atendemos'}
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                {language === 'en' ? 'Specialized pricing strategies across diverse sectors' : 'Estrategias de precios especializadas en diversos sectores'}
              </p>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '500ms' }}>
              <LogoCarousel columnCount={3} logos={industries} />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSection />

      {/* Lead Magnet Section */}
      <section id="lead-magnet" className="py-20 bg-gradient-to-br from-primary/5 via-accent/3 to-secondary/5 relative overflow-hidden">
        {/* Animated geometric shapes */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-10 w-64 h-64 border-4 border-primary/30 rounded-full animate-spin" style={{ animationDuration: '20s' }}></div>
          <div className="absolute bottom-10 left-10 w-48 h-48 border-4 border-accent/30 rotate-45 animate-pulse"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-gradient-to-r from-primary/20 to-accent/20 rounded-lg rotate-12 animate-float"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {language === 'en' ? 'Free Pricing Strategy Checklist' : 'Lista Gratuita de Estrategia de Precios'}
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                {language === 'en' ? 'Used by 300+ companies to stop margin leaks, outsmart competitors, and grow profits.' : 'Usada por 300+ empresas para detener fugas de margen, superar competidores y hacer crecer ganancias.'}
              </p>
              <div className="space-y-4">
                {[
                  language === 'en' ? '15 proven steps to build a winning pricing strategy' : '15 pasos probados para construir una estrategia ganadora de precios',
                  language === 'en' ? 'Ready-to-use competitor analysis templates' : 'Plantillas listas para usar de análisis de competidores', 
                  language === 'en' ? 'Simple methods to calculate ROI and profit impact' : 'Métodos simples para calcular ROI e impacto en ganancias'
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 animate-slide-up group hover:translate-x-2 transition-transform duration-300"
                    style={{ animationDelay: `${index * 200}ms` }}
                  >
                    <div className="w-6 h-6 bg-gradient-to-r from-success to-success/70 rounded-full flex items-center justify-center group-hover:scale-125 transition-transform duration-300">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <span className="group-hover:text-primary transition-colors duration-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-center animate-slide-up" style={{ animationDelay: '600ms' }}>
              <div className="relative">
                {/* Glowing border effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-accent/20 to-primary/20 rounded-2xl blur-xl animate-pulse"></div>
                <div className="relative">
                  <LeadMagnetForm />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        id="cta"
        className="relative py-20 text-white overflow-hidden"
        style={{
          backgroundImage: `url('/lovable-uploads/7422e3b3-0b8e-4e92-b73c-955ed069665f.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dynamic overlay with animation */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/70 via-primary/60 to-accent/70 backdrop-blur-[1px]"></div>
        
        {/* Animated particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-white/30 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            ></div>
          ))}
        </div>
        
        <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="animate-slide-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg animate-pulse">
              {language === 'en' ? 'Ready to Optimize Your Pricing?' : '¿Listo para Optimizar Tus Precios?'}
            </h2>
            <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto text-white drop-shadow-md">
              {language === 'en' 
                ? 'Book your free diagnostic session and uncover new margin and revenue opportunities — with data, not guesswork.' 
                : 'Agenda tu sesión de diagnóstico gratuito y descubre nuevas oportunidades de margen e ingresos — con datos, no conjeturas.'}
            </p>
            <div className="animate-bounce" style={{ animationDelay: '1s' }}>
              <ScheduleButton
                locale={language}
                variant="secondary"
                size="lg"
                source="index-cta"
                intent="consultation"
                className="cta-transform shadow-strong backdrop-blur-sm bg-white/95 hover:bg-white text-primary hover:scale-110 transition-all duration-300 relative overflow-hidden group"
              />
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Index;
